import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, Users, Car, AlertCircle, Ban } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransportStats() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/transport-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransports(response.data);
    } catch (error) {
      toast.error('Failed to load transport statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateTransport = async (transportId) => {
    if (!deactivateReason.trim()) {
      toast.error('Please provide a reason for deactivation');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/admin/transport/${transportId}/deactivate`,
        { reason: deactivateReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transport operator deactivated successfully');
      setDeactivateTarget(null);
      setDeactivateReason('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate transport');
    }
  };

  const handleDeactivateDriver = async (driverId) => {
    if (!deactivateReason.trim()) {
      toast.error('Please provide a reason for deactivation');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/admin/driver/${driverId}/deactivate`,
        { reason: deactivateReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver deactivated successfully');
      setDeactivateTarget(null);
      setDeactivateReason('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate driver');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500';
      case 'pending': return 'bg-amber-500';
      case 'deactivated': return 'bg-red-500';
      case 'archived': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white p-6" data-testid="transport-stats-page">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/')} className="rounded-full mb-4" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold" data-testid="page-title">Transport Statistics</h1>
              <p className="text-muted-foreground mt-1">Complete overview of all transport operators and their drivers</p>
            </div>
            <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow">
              <div className="text-center">
                <TrendingUp className="w-8 h-8 text-primary mx-auto mb-1" />
                <p className="text-2xl font-bold" data-testid="total-transports">{transports.length}</p>
                <p className="text-xs text-muted-foreground">Transports</p>
              </div>
              <div className="h-12 w-px bg-gray-200"></div>
              <div className="text-center">
                <Users className="w-8 h-8 text-blue-500 mx-auto mb-1" />
                <p className="text-2xl font-bold" data-testid="total-drivers">{transports.reduce((sum, t) => sum + t.driver_count, 0)}</p>
                <p className="text-xs text-muted-foreground">Total Drivers</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {transports.map((transport) => (
            <Card key={transport.user_id} className="shadow-lg" data-testid={`transport-card-${transport.user_id}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CardTitle className="text-2xl font-heading" data-testid={`transport-name-${transport.user_id}`}>
                        {transport.full_name}
                      </CardTitle>
                      <Badge className={getStatusColor(transport.status)} data-testid={`transport-status-${transport.user_id}`}>
                        {transport.status}
                      </Badge>
                    </div>
                    <CardDescription>
                      <span data-testid={`transport-email-${transport.user_id}`}>{transport.email}</span>
                      <span className="mx-2">•</span>
                      <span data-testid={`transport-driver-count-${transport.user_id}`}>
                        {transport.active_driver_count} active drivers / {transport.driver_count} total
                      </span>
                    </CardDescription>
                  </div>
                  {transport.status === 'active' && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="rounded-full"
                          onClick={() => setDeactivateTarget({ type: 'transport', id: transport.user_id, name: transport.full_name })}
                          data-testid={`deactivate-transport-${transport.user_id}`}
                        >
                          <Ban className="w-4 h-4 mr-2" />
                          Deactivate
                        </Button>
                      </DialogTrigger>
                      <DialogContent data-testid="deactivate-transport-dialog">
                        <DialogHeader>
                          <DialogTitle>Deactivate Transport Operator</DialogTitle>
                          <DialogDescription>
                            You are about to deactivate {transport.full_name}. Please provide a reason.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="reason">Reason for Deactivation</Label>
                            <Textarea
                              id="reason"
                              value={deactivateReason}
                              onChange={(e) => setDeactivateReason(e.target.value)}
                              placeholder="Enter the reason for deactivation"
                              rows={4}
                              data-testid="deactivate-reason-input"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            variant="destructive"
                            onClick={() => handleDeactivateTransport(transport.user_id)}
                            data-testid="confirm-deactivate-transport"
                          >
                            Deactivate
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {transport.drivers.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground" data-testid="no-drivers">No drivers yet</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {transport.drivers.map((driver) => (
                      <div
                        key={driver.driver_id}
                        className="p-4 border rounded-xl hover:shadow-md transition-all"
                        data-testid={`driver-item-${driver.driver_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold" data-testid={`driver-name-${driver.driver_id}`}>{driver.full_name}</h4>
                              <Badge variant="outline" className={getStatusColor(driver.status)} data-testid={`driver-status-${driver.driver_id}`}>
                                {driver.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground" data-testid={`driver-details-${driver.driver_id}`}>
                              {driver.email}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1" data-testid={`driver-phone-${driver.driver_id}`}>
                              {driver.phone}
                            </p>
                            {driver.vehicle_id && (
                              <div className="flex items-center gap-1 mt-1">
                                <Car className="w-3 h-3 text-emerald-500" />
                                <span className="text-xs text-emerald-600">Vehicle assigned</span>
                              </div>
                            )}
                          </div>
                          {driver.status === 'active' && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => setDeactivateTarget({ type: 'driver', id: driver.driver_id, name: driver.full_name })}
                                  data-testid={`deactivate-driver-${driver.driver_id}`}
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent data-testid="deactivate-driver-dialog">
                                <DialogHeader>
                                  <DialogTitle>Deactivate Driver</DialogTitle>
                                  <DialogDescription>
                                    You are about to deactivate {driver.full_name}. Please provide a reason.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label htmlFor="driver_reason">Reason for Deactivation</Label>
                                    <Textarea
                                      id="driver_reason"
                                      value={deactivateReason}
                                      onChange={(e) => setDeactivateReason(e.target.value)}
                                      placeholder="Enter the reason for deactivation"
                                      rows={4}
                                      data-testid="deactivate-driver-reason-input"
                                    />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button
                                    variant="destructive"
                                    onClick={() => handleDeactivateDriver(driver.driver_id)}
                                    data-testid="confirm-deactivate-driver"
                                  >
                                    Deactivate
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
